import { Component, ElementRef, HostListener, OnDestroy, ViewChild } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { ProjectsService } from 'libs/data/src/lib/services/projects/projects.service';
import { SharedService } from 'libs/data/src/lib/services/shared/shared.service';
import { SwalService } from 'libs/shared/src/lib/service/swal.service';
import { ToastrService } from 'ngx-toastr';
import { catchError, finalize, of } from 'rxjs';
import Swal from 'sweetalert2';
import { Location, ViewportScroller } from '@angular/common';
import { customDecode, customEncode } from '@piqet-angular/data';
import {
  AccessPermissionService,
  FileUploadComponent,
  FileUploadResult,
  projectsPlaceholder,
} from '@piqet-angular/shared';

@Component({
  selector: 'piqet-angular-product-detail-content',
  templateUrl: './product-detail-content.component.html',
  styleUrls: ['./product-detail-content.component.scss'],
})
export class ProductDetailContentComponent implements OnDestroy {
  productProjectForm!: FormGroup;
  projectsPlaceholder = projectsPlaceholder;
  submitted: boolean = false;
  selectCountry: any;
  defaultRegionItems: any;
  regionItems: any;
  productProjectData: any;
  showCurrencyList: any;
  isCostTurnedOff: any;
  selectedCurrency: any;
  currencyList: any;
  productConfigId!: any;

  // File upload properties
  selectedFiles: string | null = null;
  imageUrl: string | null = null;
  fileBase64: string | null = null;
  existingFileName: string | null = null;

  unitId: any;
  unitList: any;
  productScope: any;
  selectedProductScopeIds: any[] = [];
  checkRemoveImgApi = false;
  currencyItem: any;
  stopRouting: boolean = false;
  // OUTPUTS
  @ViewChild('fileUploadComponent') fileUploadComponent!: FileUploadComponent;
  productImage: any;
  hasUnsavedChanges: boolean = false;
  initialFormValue: any;
  private initialIsCostTurnedOff: boolean = false;
  private initialProductScope: any[] = [];

  private isPopupOpen = false;
  private navigationBlocked = false;
  private originalPushState: any;

  showEdit = false;
  constructor(
    private readonly fb: FormBuilder,
    private readonly projectsService: ProjectsService,
    private readonly route: ActivatedRoute,
    private readonly toaster: ToastrService,
    private readonly viewportScroller: ViewportScroller,
    private readonly router: Router,
    private readonly swalService: SwalService,
    private readonly sharedService: SharedService,
    private readonly location: Location,
    private readonly accessService: AccessPermissionService
  ) {
    this.originalPushState = history.pushState;
    history.pushState = (...args) => {
      if (this.navigationBlocked) {
        return;
      }
      return this.originalPushState.apply(history, args);
    };
    localStorage.removeItem('scenarioName');
    this.sharedService.scenarioNameFunc('');

    this.route.queryParams.subscribe(async (values: any) => {
      this.imageUrl = '';
      this.removeImage();
    });
  }

  @HostListener('window:popstate', ['$event'])
  onPopState(event: PopStateEvent) {
    if (this.isPopupOpen) {
      event.preventDefault();
      Swal.close();
      // Restore the URL without triggering another popstate event
      this.location.forward();
      return false;
    }
  }

  // Add HostListener for browser back/forward buttons
  @HostListener('window:beforeunload', ['$event'])
  handleBeforeUnload(event: BeforeUnloadEvent) {
    if (this.hasUnsavedChanges || this.isPopupOpen) {
      event.preventDefault();
    }
  }

  async ngOnInit() {
    this.viewportScroller.scrollToPosition([0, 0]);
    this.showEdit = this.accessService.hasPermission('edit', '');
    this.initializeForm();
    // const url = location.href.split('/');
    // this.productConfigId = url[6];
    const url = location.href.split('/');
    this.productConfigId = Number(atob(decodeURIComponent(customDecode(url[6]))));
    await this.getProductProjectData();
    this.bindFormData();
    this.initialFormValue = this.productProjectForm.value;
    this.initialIsCostTurnedOff = this.isCostTurnedOff;
    this.initialProductScope = JSON.parse(JSON.stringify(this.productScope));
    this.setCurrency();
    this.productProjectForm.patchValue({ currency: this.currencyItem });
    this.productProjectForm.valueChanges.subscribe(() => {
      this.checkFormChanges();
    });
  }

  checkFormChanges() {
    const currentFormValue = this.productProjectForm.value;
    const formChanged = JSON.stringify(currentFormValue) !== JSON.stringify(this.initialFormValue);
    const costSwitchChanged = this.isCostTurnedOff !== this.initialIsCostTurnedOff;

    // Check if product scope has changed
    const scopeChanged = this.initialProductScope.some(initial => {
      const current = this.productScope.find((p: any) => p.id === initial.id);
      return current?.selected !== initial.selected;
    });

    this.hasUnsavedChanges =
      formChanged || this.checkRemoveImgApi || costSwitchChanged || scopeChanged;
  }

  async setCurrency() {
    await this.currencyList?.map((data: any) => {
      if (data?.id == this.productProjectData?.currencyId) {
        this.currencyItem = data;
        return;
      }
    });
  }

  initializeForm() {
    this.productProjectForm = this.fb.group({
      productName: ['', [Validators.required]],
      country: [null],
      defaultCountry: [null, Validators.required],
      currency: [null],
      unit: [null, Validators.required],
      description: [''],
      goalAndScope: [''],
      otherLimitations: [''],
      interpretation: [''],
    });
  }

  async getProductProjectData() {
    let response = await this.projectsService.getMyAllProductProjectModalEditData(
      this.productConfigId,
      false,
      0
    );
    this.productProjectData = response.data;
    this.defaultRegionItems = this.productProjectData?.countryItems;
    this.regionItems = this.productProjectData?.countryItems;
    this.currencyList = this.productProjectData?.currencyItems;
    this.isCostTurnedOff = !this.productProjectData?.isTurnOffCost;
    this.showCurrencyList = this.isCostTurnedOff;
    this.unitId = this.productProjectData?.unitId;
    this.unitList = this.productProjectData.unitList;
    this.productImage = this.productProjectData?.productImage;
    const urlParts = this.productImage?.split('/');
    if (urlParts) {
      const fileName = urlParts[urlParts?.length - 1];
      this.existingFileName = fileName ?? null;
      this.selectedFiles = fileName ?? null;
    }
    this.productScope = this.productProjectData.productScopes;
    this.selectedProductScopeIds = this.productScope
      .filter((scope: any) => scope.selected === true)
      .map((scope: any) => scope.id);
    this.selectCountry = this.regionItems?.filter((country: any) =>
      this.productProjectData?.productCountries?.includes(country.id)
    );
  }

  bindFormData() {
    // Find the default country object by id
    const defaultCountry = this.defaultRegionItems?.find(
      (country: any) => country.id === this.productProjectData?.countryId
    );

    // Find the selected country objects by ids
    const selectedCountries = this.regionItems?.filter((country: any) =>
      this.productProjectData?.productCountries?.includes(country.id)
    );

    // bind currency id if cost is turned on
    const currencyId = this.productProjectData?.currencyId;
    this.selectedCurrency = currencyId;

    const formValues: any = {
      productName: this.productProjectData?.name,
      country: selectedCountries,
      defaultCountry: defaultCountry,
      unit: this.productProjectData?.unitId,
      description: this.productProjectData?.description,
      goalAndScope: this.productProjectData?.goalAndScope,
      otherLimitations: this.productProjectData?.otherAssumptionsOrLimitation,
      interpretation: this.productProjectData?.interpretation,
    };

    // Only bind currency if cost is turned on
    if (this.isCostTurnedOff) {
      formValues.currency = this.currencyList?.find((c: any) => c.id === currencyId);
      this.productProjectForm.get('currency')?.setValidators([Validators.required]);
    } else {
      this.productProjectForm.get('currency')?.clearValidators();
    }
    this.productProjectForm.get('currency')?.updateValueAndValidity();

    this.productProjectForm.patchValue(formValues);
  }

  commonInputSearch(term: string, item: any) {
    return item.name.toLowerCase().includes(term.toLowerCase());
  }

  defalutCountry(selectCountry: any) {
    if (this.productProjectForm.controls['defaultCountry']?.value?.id) {
      const data = selectCountry.find((data: any) => {
        if (data.id == this.productProjectForm.controls['defaultCountry']?.value.id) {
          return true;
        }
      });
      if (!data) {
        this.productProjectForm.controls['defaultCountry'].setValue(null);
      }
    }
    this.selectCountry = selectCountry.length == 0 ? this.defaultRegionItems : selectCountry;
  }

  costTurnOff(event: any) {
    if (event.target.checked && !this.productProjectData?.currencyId) {
      // FOR setting USD as default currency if none is selected
      this.productProjectForm
        ?.get('currency')
        ?.setValue(this.productProjectData?.currencyItems?.find((x: any) => x.id == 1));
    }
    this.showCurrencyList = event.target.checked;
    this.checkFormChanges();
  }

  selectCurrency(event: any) {
    if (event?.id) {
      this.selectedCurrency = event?.id;
    }
  }

  selectedProductScope(event: any, id: any) {
    const selectedItem = this.productScope.find((scope: any) => scope.id === id);

    // Prevent unchecking Ingredients
    if (selectedItem?.name?.toLowerCase() === 'ingredients' && !event.target.checked) {
      event.preventDefault();
      event.target.checked = true;
      return;
    }

    let clone = [...this.productScope];
    clone.forEach((x: any) => {
      if (x.id === id) {
        x.selected = event.target.checked;
      }
    });
    this.productScope = clone;
    this.selectedProductScopeIds = this.productScope
      .filter((scope: any) => scope.selected !== false)
      .map((scope: any) => scope.id);
    this.checkFormChanges();
  }

  onFileSelected(result: FileUploadResult): void {
    if (result.isValid) {
      this.selectedFiles = result.fileName;
      this.fileBase64 = result.fileBase64;
      this.imageUrl = result.fileUrl;
      this.checkRemoveImgApi = true;
      this.checkFormChanges();
    }
  }

  deleteConfirmation() {
    // If there's a backend image, show confirmation dialog
    if (this.productImage) {
      this.swalService
        .confirm(
          'Are you sure?',
          'Do you really want to delete this image? This process cannot be undone.'
        )
        .then(confirmed => {
          if (confirmed) {
            this.removeProductImage();
          }
        });
    } else {
      // If it's just a newly selected file (not from BE), remove directly without confirmation
      this.removeImage();
      this.toaster.success('Image has been removed');
    }
  }

  removeProductImage() {
    this.projectsService
      .removeProductConfigurationImage(this.productConfigId)
      .subscribe((res: any) => {
        if (res.messageType) {
          this.toaster.error(res.message);
        } else {
          this.toaster.success(res.message);
          this.imageUrl = null;
          this.productImage = '';
          this.existingFileName = null;
          this.selectedFiles = null;

          // Reset the file upload component
          if (this.fileUploadComponent) {
            this.fileUploadComponent.clearFile();
          }

          this.checkFormChanges();
        }
      });
  }

  removeImage() {
    this.selectedFiles = null;
    this.fileBase64 = null;
    this.imageUrl = null;
    this.existingFileName = null;

    // Reset the file upload component if it exists
    if (this.fileUploadComponent) {
      this.fileUploadComponent.clearFile();
    }
    this.checkFormChanges();
  }

  changeUnit(eve: any) {
    if (eve?.id) {
      this.unitId = eve?.id;
    }
  }

  updateProductProject(): void {
    this.submitted = true;
    if (!this.productProjectForm.valid) {
      this.stopRouting = true;
      return;
    }
    const projectPayload = {
      productId: this.productProjectData.productId ?? 0,
      projectId: this.productProjectData.projectId ?? 0,
      productConfigId: this.productProjectData.productConfigId ?? 0,
      productCountries: this.productProjectForm.controls['country'].value?.map(
        (country: any) => country.id
      ),
      name: this.productProjectForm.controls['productName'].value,
      countryId: this.productProjectForm.controls['defaultCountry'].value?.id,
      productContentsFile: this.selectedFiles,
      productContentsImage: this.fileBase64,
      unitId: this.unitId,
      description: this.productProjectForm.controls['description'].value,
      currencyId: this.selectedCurrency ?? this.currencyItem?.id,
      isTurnOffCost: !this.isCostTurnedOff,
      selectedScopes: this.selectedProductScopeIds,
      goalAndScope: this.productProjectForm.controls['goalAndScope'].value,
      otherAssumptionsOrLimitation: this.productProjectForm.controls['otherLimitations'].value,
      interpretation: this.productProjectForm.controls['interpretation'].value,
    };
    this.projectsService
      .saveProjectProduct(projectPayload)
      .pipe(
        catchError((error: any) =>
          of(this.toaster.error('An error occurred. Please try again later.'))
        ),
        finalize(() => {
          this.sharedService.accessProductSideBarData(() => {});
        })
      )
      .subscribe(res => {
        this.checkRemoveImgApi = false;
        this.stopRouting = false;
        this.hasUnsavedChanges = false;

        if (!res.messageType) {
          this.initialFormValue = { ...this.productProjectForm.value };
          this.initialIsCostTurnedOff = this.isCostTurnedOff;
          this.initialProductScope = JSON.parse(JSON.stringify(this.productScope));
          this.hasUnsavedChanges = false;
        }
        res.messageType ? this.toaster.error(res.message) : this.toaster.success(res.message);
      });
  }

  async nextPage() {
    if (!this.stopRouting) {
      const productConfigId: any = localStorage.getItem('productConfigId');

      this.router.navigate(
        [`projects/product-configuration/${customEncode(btoa(productConfigId))}/product-list`],
        {
          state: { fromSettings: true },
        }
      );
    }
  }

  async canDeactivate(): Promise<boolean> {
    if (!this.hasUnsavedChanges) {
      this.sharedService.accessProductSideBarData(() => {});
      return true;
    }

    return new Promise<boolean>(resolve => {
      this.isPopupOpen = true;
      this.navigationBlocked = true;
      this.isPopupOpen = true;
      this.swalService
        .confirm(
          'Unsaved Changes',
          'You have unsaved changes. Would you like to save them before leaving?'
        )
        .then(result => {
          this.isPopupOpen = false;
          if (result) {
            this.updateProductProject();
            this.navigationBlocked = false;
            resolve(true);
          } else {
            this.isCostTurnedOff = this.initialIsCostTurnedOff;
            this.productScope = JSON.parse(JSON.stringify(this.initialProductScope));
            this.hasUnsavedChanges = false;
            this.sharedService.accessProductSideBarData(() => {});
            this.isPopupOpen = false;
            this.navigationBlocked = false;
            resolve(true);
          }
        })
        .catch(() => {
          this.isPopupOpen = false;
          this.navigationBlocked = false;
          resolve(false);
        });
    });
  }

  ngOnDestroy() {
    // Restore original pushState function
    if (this.originalPushState) {
      history.pushState = this.originalPushState;
    }
  }

  isIngredientScope(item: any): boolean {
    return item.name?.toLowerCase() === 'ingredients';
  }
}
